/**
 * API Service Layer for VulnZero Frontend
 *
 * Centralized API client with:
 * - Request/response interceptors
 * - Authentication token handling
 * - Error handling and retries
 * - Request/response logging
 */

import axios from 'axios'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

// API Base URL — use relative path in dev (Vite proxy handles /api -> backend)
// In production, set VITE_API_BASE_URL to the actual backend URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// ---------------------------------------------------------------------------
// Session storage hardening
//
// The access token lives ONLY in module memory (lost on reload -> the app
// silently re-authenticates via the refresh cookie). The 7-day refresh token
// lives ONLY in an HttpOnly cookie set by the server — JavaScript can never
// read it, so an XSS cannot exfiltrate a durable credential.
//
// Legacy localStorage credentials are REMOVED once at import (never
// re-imported): browsers upgrading from the old scheme simply log in again.
// ---------------------------------------------------------------------------
let accessToken = null

export const session = {
  setAccessToken: (token) => {
    accessToken = token
  },
  getAccessToken: () => accessToken,
  clearAccessToken: () => {
    accessToken = null
  },
}

try {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('refresh_token')
} catch {
  /* storage unavailable (private mode) — nothing to clean */
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // send/receive the HttpOnly refresh cookie
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Access token from MEMORY (never persisted storage)
    const token = accessToken

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add request ID for tracing
    config.headers['X-Request-ID'] = generateRequestId()

    // Log request
    logger.apiRequest(config.method || 'GET', config.url, config.params || config.data)

    return config
  },
  (error) => {
    logger.error('Request interceptor error', error)
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Log response
    logger.apiResponse(response.config.method || 'GET', response.config.url, response.data)
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Canceled / aborted requests are NOT failures. They fire constantly during
    // normal use — navigating away, a component unmounting, or React StrictMode
    // double-invoking effects all abort in-flight requests, and each one lands
    // here with no `error.response`. Surfacing a toast for them produced the
    // "Network error" flood that stacked dozens deep during rapid navigation.
    // Swallow them silently before any toast/log.
    if (
      axios.isCancel?.(error) ||
      error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      error.name === 'AbortError'
    ) {
      return Promise.reject(error)
    }

    // Log error
    logger.apiError(
      originalRequest?.method || 'GET',
      originalRequest?.url || 'unknown',
      error.response?.status || 0,
      error.response?.data
    )

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Single-flight refresh: concurrent 401s share ONE refresh call
        // (parallel refreshes would rotate the cookie under each other).
        if (!_refreshPromise) {
          _refreshPromise = axios
            .post(
              `${API_BASE_URL}/api/v1/auth/refresh`,
              {},
              { withCredentials: true }
            )
            .then((response) => {
              accessToken = response.data.access_token
              return accessToken
            })
            .finally(() => {
              _refreshPromise = null
            })
        }
        const fresh = await _refreshPromise

        // Retry original request with the new token
        originalRequest.headers.Authorization = `Bearer ${fresh}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed - clear session and redirect to login.
        // NEVER hard-navigate when we are already on /login: that turns
        // a failed silent re-auth into a full-page RELOAD LOOP (each
        // reload re-mounts AuthContext, which retries the refresh).
        accessToken = null
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    // Every toast below uses a FIXED id so react-hot-toast REPLACES the existing
    // one instead of stacking a new copy per failed request/poll cycle — a single
    // failing background poller can otherwise pile up dozens of identical toasts.
    const status = error.response?.status

    if (status === 403) {
      toast.error('Access denied. You do not have permission to perform this action.', { id: 'http-403' })
    } else if (status === 429) {
      // Rate limited
      toast.error('Too many requests. Please slow down.', { id: 'http-429' })
    } else if (status >= 500) {
      // Server error
      toast.error('Server error. Please try again later.', { id: 'http-5xx' })
    } else if (!error.response) {
      // No response: real connectivity loss, or a timeout. Deduped so it can't flood.
      toast.error('Network error. Please check your connection.', { id: 'network-error' })
    }
    // 404s are intentionally NOT toasted globally: they're usually expected
    // (an optional/probe endpoint, a not-yet-created resource) and each page
    // handles its own "not found" state. A global toast just added noise.

    return Promise.reject(error)
  }
)

// Helper: Generate unique request ID
function generateRequestId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// In-flight refresh shared by concurrent 401 handlers (single-flight).
let _refreshPromise = null

// API Methods

/**
 * Vulnerabilities API
 */
export const vulnerabilitiesApi = {
  /**
   * List all vulnerabilities with optional filters
   * @param {Object} params - Query parameters (severity, status, page, limit)
   */
  list: (params = {}) => apiClient.get('/api/v1/vulnerabilities/', { params }),

  /**
   * Get a single vulnerability by ID
   * @param {string|number} id - Vulnerability ID
   */
  get: (id) => apiClient.get(`/api/v1/vulnerabilities/${id}`),

  /**
   * Create a new vulnerability
   * @param {Object} data - Vulnerability data
   */
  create: (data) => apiClient.post('/api/v1/vulnerabilities/', data),

  /**
   * Update a vulnerability
   * @param {string|number} id - Vulnerability ID
   * @param {Object} data - Updated data
   */
  update: (id, data) => apiClient.patch(`/api/v1/vulnerabilities/${id}`, data),

  /**
   * Delete a vulnerability
   * @param {string|number} id - Vulnerability ID
   */
  delete: (id) => apiClient.delete(`/api/v1/vulnerabilities/${id}`),

  /**
   * Mark vulnerability as false positive
   * @param {string|number} id - Vulnerability ID
   */
  markFalsePositive: (id) => apiClient.post(`/api/v1/vulnerabilities/${id}/false-positive`),

  stats: () => apiClient.get('/api/v1/vulnerabilities/stats'),
  getAssets: (id) => apiClient.get(`/api/v1/vulnerabilities/${id}/assets`),

  /** Plain-English CVE summary (deterministic, no LLM). */
  summary: (id) => apiClient.get(`/api/v1/vulnerabilities/${id}/summary`),
  /** Suggested triage verdict (fix/defer/false_positive/risk_accept/duplicate) + signals. */
  triage: (id) => apiClient.get(`/api/v1/vulnerabilities/${id}/triage`),
  /** Exact remediation command (needs a known fixed version). */
  remediationSuggestion: (id, params = {}) =>
    apiClient.get(`/api/v1/vulnerabilities/${id}/remediation-suggestion`, { params }),
  enrich: (id) => apiClient.post(`/api/v1/vulnerabilities/${id}/enrich`),

  /**
   * Get agent reasoning iterations for a vulnerability
   * @param {string|number} id - Vulnerability ID
   * @param {Object} params - { limit, offset }
   */
  getReasoning: (id, params = {}) =>
    apiClient.get(`/api/v1/vulnerabilities/${id}/reasoning`, { params }),
}

/**
 * Patches API
 */
export const patchesApi = {
  /**
   * List all patches with optional filters
   * @param {Object} params - Query parameters (status, vulnerability_id, page, limit)
   */
  list: (params = {}) => apiClient.get('/api/v1/patches/', { params }),

  /** Aggregate patch stats (counts by status, avg confidence). */
  stats: () => apiClient.get('/api/v1/patches/stats/summary'),

  /**
   * Get a single patch by ID
   * @param {string|number} id - Patch ID
   */
  get: (id) => apiClient.get(`/api/v1/patches/${id}`),

  /**
   * Generate a new patch for a vulnerability
   * @param {Object} data - { vulnerability_id, patch_type }
   */
  generate: (data) => apiClient.post('/api/v1/patches/generate', data),

  /**
   * Approve a patch
   * @param {string|number} id - Patch ID
   */
  approve: (id) => apiClient.post(`/api/v1/patches/${id}/approve`),

  /**
   * Reject a patch
   * @param {string|number} id - Patch ID
   * @param {string} reason - Rejection reason
   */
  reject: (id, reason) => apiClient.post(`/api/v1/patches/${id}/reject`, { rejection_reason: reason }),

  /**
   * Test a patch in digital twin
   * @param {string|number} id - Patch ID
   */
  test: (id) => apiClient.post(`/api/v1/patches/${id}/test`),

  /** Change-impact (blast radius) report for one patch — the CAB artifact. */
  blastRadius: (id) => apiClient.get(`/api/v1/patches/${id}/blast-radius`),

  /**
   * Approve many patches at once.
   * @param {number[]} patchIds - Patch ids to approve
   */
  bulkApprove: (patchIds) =>
    apiClient.post('/api/v1/patches/bulk/approve', { patch_ids: patchIds }),

  /**
   * Reject many patches at once with a shared reason.
   * @param {number[]} patchIds
   * @param {string} reason
   */
  bulkReject: (patchIds, reason) =>
    apiClient.post('/api/v1/patches/bulk/reject', {
      patch_ids: patchIds,
      rejection_reason: reason,
    }),

  /**
   * Preview the "safe batch" — patches that meet the autonomy bar and can
   * be approved with one click.
   * @param {Object} criteria - { min_confidence, require_test_passed,
   *                               allowed_severities, max_batch }
   */
  previewSafeBatch: (criteria = {}) =>
    apiClient.post('/api/v1/patches/safe-batch/preview', {
      min_confidence: criteria.min_confidence ?? 0.85,
      require_test_passed: criteria.require_test_passed ?? true,
      allowed_severities: criteria.allowed_severities ?? ['critical', 'high'],
      max_batch: criteria.max_batch ?? 100,
    }),

  /**
   * Queue patch generation for many vulnerabilities at once.
   * @param {number[]} vulnerabilityIds
   */
  bulkGenerate: (vulnerabilityIds) =>
    apiClient.post('/api/v1/patches/bulk/generate', {
      vulnerability_ids: vulnerabilityIds,
    }),
}

/**
 * Deployments API
 */
export const deploymentsApi = {
  /**
   * List all deployments with optional filters
   * @param {Object} params - Query parameters (status, environment, page, limit)
   */
  list: (params = {}) => apiClient.get('/api/v1/deployments/', { params }),

  /**
   * Get a single deployment by ID
   * @param {string|number} id - Deployment ID
   */
  get: (id) => apiClient.get(`/api/v1/deployments/${id}`),

  /**
   * Create a new deployment
   * @param {Object} data - { patch_id, strategy, target_environment }
   */
  create: (data) => apiClient.post('/api/v1/deployments/', data),

  /**
   * Get deployment status
   * @param {string|number} id - Deployment ID
   */
  status: (id) => apiClient.get(`/api/v1/deployments/${id}/status`),

  /**
   * Rollback a deployment
   * @param {string|number} id - Deployment ID
   * @param {string} rollbackReason - Reason for rollback (required)
   */
  rollback: (id, rollbackReason) =>
    apiClient.post(`/api/v1/deployments/${id}/rollback`, {
      rollback_reason: rollbackReason,
    }),

  /**
   * Rollback many deployments at once with a shared reason.
   * @param {number[]} deploymentIds
   * @param {string} rollbackReason
   */
  bulkRollback: (deploymentIds, rollbackReason) =>
    apiClient.post('/api/v1/deployments/bulk/rollback', {
      deployment_ids: deploymentIds,
      rollback_reason: rollbackReason,
    }),

  /**
   * Ask the rollback root-cause analyzer to label a deployment failure.
   * `error_output` is passed as a query param because the backend route
   * treats it as a primitive-with-default, not a JSON body field.
   * @param {string|number} id - Deployment ID
   * @param {string} errorOutput - Raw error text from execution logs
   */
  analyzeRollback: (id, errorOutput = '') => {
    const params = new URLSearchParams({ error_output: errorOutput })
    return apiClient.post(
      `/api/v1/deployments/${id}/analyze-rollback?${params.toString()}`,
    )
  },

  /**
   * Get recent rollback analyses + category stats from the in-memory
   * rollback analyzer (resets on backend restart).
   * @param {number} limit
   */
  rollbackHistory: (limit = 50) =>
    apiClient.get('/api/v1/deployments/rollbacks/history', { params: { limit } }),

  /**
   * Pause a deployment
   * @param {string|number} id - Deployment ID
   */
  pause: (id) => apiClient.post(`/api/v1/deployments/${id}/pause`),

  /**
   * Resume a deployment
   * @param {string|number} id - Deployment ID
   */
  resume: (id) => apiClient.post(`/api/v1/deployments/${id}/resume`),
}

/**
 * Monitoring API
 */
export const monitoringApi = {
  /**
   * Get system health status
   */
  health: () => apiClient.get('/health'),

  /**
   * Get system metrics
   */
  metrics: () => apiClient.get('/api/v1/monitoring/metrics'),

  /**
   * Get alerts
   * @param {Object} params - Query parameters (severity, acknowledged, page, limit)
   */
  alerts: (params = {}) => apiClient.get('/api/v1/monitoring/alerts', { params }),

  /**
   * Acknowledge an alert
   * @param {string|number} id - Alert ID
   */
  acknowledgeAlert: (id) => apiClient.post(`/api/v1/monitoring/alerts/${id}/acknowledge`),

  /**
   * Get deployment analytics
   * @param {Object} params - Query parameters (start_date, end_date)
   */
  analytics: (params = {}) => apiClient.get('/api/v1/monitoring/analytics', { params }),
}

/**
 * Dashboard API
 */
export const dashboardApi = {
  /**
   * Get dashboard statistics
   */
  stats: () => apiClient.get('/api/v1/dashboard/stats'),

  /**
   * Composite fleet-posture score for the dashboard hero tile.
   * Returns score (0-100), letter grade, headline, factor list and
   * drill-down sub-metrics.
   */
  fleetPosture: () => apiClient.get('/api/v1/dashboard/fleet-posture'),

  /**
   * Get MTTR trend over time
   * @param {number} days - Number of days to look back
   */
  mttrTrend: (days = 30) => apiClient.get('/api/v1/dashboard/mttr-trend', { params: { days } }),

  /**
   * Get rich MTTR analytics (p50/p90/p99 + severity breakdown + SLA compliance).
   * @param {number} days - Lookback window in days.
   */
  mttrAnalytics: (days = 90) => apiClient.get('/api/v1/dashboard/mttr-analytics', { params: { days } }),

  /** Live SLA compliance snapshot for open vulnerabilities. */
  slaCompliance: () => apiClient.get('/api/v1/dashboard/sla-compliance'),

  /** Remediation ROI — dollar case for autonomous remediation (transparent assumptions). */
  remediationRoi: (params = {}) => apiClient.get('/api/v1/dashboard/remediation-roi', { params }),

  /**
   * Get executive summary narrative
   */
  executiveSummary: () => apiClient.get('/api/v1/dashboard/executive-summary'),
  downloadExecutiveSummary: () => apiClient.get('/api/v1/dashboard/executive-summary/download', { responseType: 'blob' }),
  downloadOperationalReport: (hours = 24) => apiClient.get('/api/v1/dashboard/operational-report/download', { params: { hours }, responseType: 'blob' }),
}

// ==========================================================================
// Wave 6 / Wave 7 API clients
// ==========================================================================

/** KEV auto-queue (CISA Known Exploited Vulnerabilities). */
export const kevApi = {
  list: (params = {}) => apiClient.get('/api/v1/kev/', { params }),
  summary: () => apiClient.get('/api/v1/kev/summary'),
  refresh: () => apiClient.post('/api/v1/kev/refresh'),
}

/** Cross-tenant MTTR benchmarks. */
export const benchmarksApi = {
  bucket: (os, severity, window_days = 90) =>
    apiClient.get('/api/v1/benchmarks/bucket', { params: { os, severity, window_days } }),
  compare: (os, severity, window_days = 90) =>
    apiClient.get('/api/v1/benchmarks/compare', { params: { os, severity, window_days } }),
  optIn: () => apiClient.post('/api/v1/benchmarks/opt-in'),
  optOut: () => apiClient.post('/api/v1/benchmarks/opt-out'),
}

/** EU CRA Article 14 reporting. */
export const craApi = {
  deadlines: () => apiClient.get('/api/v1/cra/deadlines'),
  report: (vulnId, kind = 'notification', format = 'json') =>
    apiClient.get(`/api/v1/cra/vulnerabilities/${vulnId}/report`, {
      params: { kind, format },
    }),
}

/** OpenVEX ingestion. */
export const vexApi = {
  coverage: () => apiClient.get('/api/v1/vex/coverage'),
  upload: (file) => {
    const form = new FormData()
    form.append('file', file)
    return apiClient.post('/api/v1/vex/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

/** Vulnerability groups (root-cause rollup). */
export const vulnerabilityGroupsApi = {
  list: (params = {}) => apiClient.get('/api/v1/vulnerability-groups/', { params }),
  get: (key, group_by = 'cve') =>
    apiClient.get(`/api/v1/vulnerability-groups/${key}`, { params: { group_by } }),
}

/** Playbook marketplace trust signals. */
export const marketplaceApi = {
  trust: (cveId) => apiClient.get(`/api/v1/marketplace/trust/${cveId}`),
  popular: (window_days = 90, limit = 20) =>
    apiClient.get('/api/v1/marketplace/popular', { params: { window_days, limit } }),
  recommend: (params) => apiClient.get('/api/v1/marketplace/recommend', { params }),
}

/** Natural-language vulnerability query. */
export const nlQueryApi = {
  parse: (q) => apiClient.post('/api/v1/nl-query/parse', { q }),
}


/**
 * Settings/Configuration API
 */
export const settingsApi = {
  /**
   * Get user settings
   */
  get: () => apiClient.get('/api/v1/settings'),

  /**
   * Update user settings
   * @param {Object} data - Settings data
   */
  update: (data) => apiClient.patch('/api/v1/settings', data),

  /**
   * Get integrations
   */
  integrations: () => apiClient.get('/api/v1/settings/integrations'),

  /**
   * Update integration
   * @param {string} name - Integration name
   * @param {Object} config - Integration configuration
   */
  updateIntegration: (name, config) => apiClient.patch(`/api/v1/settings/integrations/${name}`, config),

  /**
   * Test integration connection
   * @param {string} name - Integration name
   */
  testIntegration: (name) => apiClient.post(`/api/v1/settings/integrations/${name}/test`),
}

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Login
   * @param {Object} credentials - { username, password }
   * On success the caller (AuthContext) stores the access token in MEMORY
   * via session.setAccessToken; the refresh token arrives ONLY as an
   * HttpOnly cookie the server sets on this response.
   */
  login: (credentials) => apiClient.post('/api/v1/auth/login', credentials),

  /**
   * Logout — revokes the access token (Authorization header) and the
   * refresh token (HttpOnly cookie), clears the cookie server-side, and
   * drops the in-memory access token.
   */
  logout: async () => {
    try {
      await apiClient.post('/api/v1/auth/logout')
    } catch (e) {
      // Best-effort — still clear local state even if server call fails
    }
    session.clearAccessToken()
  },

  /**
   * Refresh the access token using the HttpOnly refresh cookie
   * (no credential in the request body or storage).
   */
  refresh: () => apiClient.post('/api/v1/auth/refresh'),

  /**
   * Get current user
   */
  me: () => apiClient.get('/api/v1/auth/me'),

  /**
   * Change the current user's own password
   * @param {Object} data - { current_password, new_password }
   */
  changePassword: (data) => apiClient.post('/api/v1/auth/change-password', data),

  /**
   * Register new user (Admin only)
   * @param {Object} data - { username, email, password, full_name, role }
   */
  register: (data) => apiClient.post('/api/v1/auth/register', data),

  /**
   * List all users (Admin only)
   * @param {Object} params - Query parameters { skip, limit }
   */
  listUsers: (params = {}) => apiClient.get('/api/v1/auth/users', { params }),

  /**
   * Delete user (Admin only)
   * @param {number} userId - User ID
   */
  deleteUser: (userId) => apiClient.delete(`/api/v1/auth/users/${userId}`),
}

/**
 * Scan API - Code scanning and vulnerability detection
 */
export const scanApi = {
  /**
   * Upload files for vulnerability scanning
   * @param {FormData} formData - FormData with files
   * @param {Function} onProgress - Progress callback
   */
  upload: (formData, onProgress) => apiClient.post('/api/v1/scan/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  }),

  /**
   * Get scan status
   * @param {string} scanId - Scan ID
   */
  getStatus: (scanId) => apiClient.get(`/api/v1/scan/status/${scanId}`),

  /**
   * Get scan results
   * @param {string} scanId - Scan ID
   */
  getResults: (scanId) => apiClient.get(`/api/v1/scan/results/${scanId}`),

  /**
   * Get scan history
   * @param {number} limit - Number of recent scans
   */
  getHistory: (limit = 10) => apiClient.get('/api/v1/scan/history', { params: { limit } }),
}

/**
 * Pipeline API
 */
export const pipelineApi = {
  run: (data) => apiClient.post('/api/v1/pipeline/run', data),
  runAll: () => apiClient.post('/api/v1/pipeline/run-all'),
  status: () => apiClient.get('/api/v1/pipeline/status'),
  approvalQueue: (params = {}) => apiClient.get('/api/v1/pipeline/approval-queue', { params }),
  reviewDecision: (vulnId, data) => apiClient.post(`/api/v1/pipeline/review/${vulnId}/decision`, data),
  getLiveState: () => apiClient.get('/api/v1/pipeline/live'),
}

/**
 * Tickets API
 */
export const ticketsApi = {
  list: (params = {}) => apiClient.get('/api/v1/tickets/', { params }),
  get: (id) => apiClient.get(`/api/v1/tickets/${id}`),
  stats: () => apiClient.get('/api/v1/tickets/stats'),
  addNote: (id, note) => apiClient.post(`/api/v1/tickets/${id}/note`, { note }),
  override: (id) => apiClient.post(`/api/v1/tickets/${id}/override`),
  assign: (id, assignedTo) => apiClient.post(`/api/v1/tickets/${id}/assign`, { assigned_to: assignedTo }),
  bulkAssign: (ticketIds, assignedTo) =>
    apiClient.post('/api/v1/tickets/bulk/assign', { ticket_ids: ticketIds, assigned_to: assignedTo }),
  bulkOverride: (ticketIds, reason) =>
    apiClient.post('/api/v1/tickets/bulk/override', { ticket_ids: ticketIds, reason }),
  bulkNote: (ticketIds, note) =>
    apiClient.post('/api/v1/tickets/bulk/note', { ticket_ids: ticketIds, note }),
}

/**
 * Playbooks API
 */
export const playbooksApi = {
  list: (params = {}) => apiClient.get('/api/v1/playbooks/', { params }),
  get: (id) => apiClient.get(`/api/v1/playbooks/${id}`),
  create: (data) => apiClient.post('/api/v1/playbooks/', data),
  delete: (id) => apiClient.delete(`/api/v1/playbooks/${id}`),
  stats: () => apiClient.get('/api/v1/playbooks/stats/summary'),
}

/**
 * Policies API
 */
export const policiesApi = {
  list: () => apiClient.get('/api/v1/policies/'),
  create: (data) => apiClient.post('/api/v1/policies/', data),
  update: (id, data) => apiClient.put(`/api/v1/policies/${id}`, data),
  delete: (id) => apiClient.delete(`/api/v1/policies/${id}`),
}

/**
 * Tenants API
 */
export const tenantsApi = {
  list: () => apiClient.get('/api/v1/tenants/'),
  get: (id) => apiClient.get(`/api/v1/tenants/${id}`),
  create: (data) => apiClient.post('/api/v1/tenants/', data),
  delete: (id) => apiClient.delete(`/api/v1/tenants/${id}`),
}

/**
 * Connectors API
 */
export const connectorsApi = {
  list: () => apiClient.get('/api/v1/connectors/'),
  types: () => apiClient.get('/api/v1/connectors/types'),
  create: (data) => apiClient.post('/api/v1/connectors/', data),
  test: (id) => apiClient.post(`/api/v1/connectors/${id}/test`),
  sync: (id) => apiClient.post(`/api/v1/connectors/${id}/sync`),
  delete: (id) => apiClient.delete(`/api/v1/connectors/${id}`),
}

/**
 * Outcomes API
 */
export const outcomesApi = {
  record: (data) => apiClient.post('/api/v1/outcomes/record', data),
}

/**
 * Exceptions API
 */
export const exceptionsApi = {
  list: (params = {}) => apiClient.get('/api/v1/exceptions/', { params }),
  create: (data) => apiClient.post('/api/v1/exceptions/', data),
  approve: (id) => apiClient.post(`/api/v1/exceptions/${id}/approve`),
  revoke: (id, data) => apiClient.post(`/api/v1/exceptions/${id}/revoke`, data),
  checkExpired: () => apiClient.post('/api/v1/exceptions/check-expired'),
}

/**
 * MSSP API — manage client tenants (parent/MSSP_ADMIN only)
 */
export const msspApi = {
  listChildren: () => apiClient.get('/api/v1/mssp/children'),
  provisionClient: (data) => apiClient.post('/api/v1/mssp/clients', data),
  aggregateMetrics: () => apiClient.get('/api/v1/mssp/metrics/aggregate'),
}

/**
 * Assets API
 */
export const assetsApi = {
  list: (params = {}) => apiClient.get('/api/v1/assets/', { params }),
  get: (id) => apiClient.get(`/api/v1/assets/${id}`),
  discover: (provider) => apiClient.post(`/api/v1/assets/discover?provider=${provider}`),
  discoverNetwork: (subnet = '10.0.0.0/24') => apiClient.post(`/api/v1/assets/discover/network?subnet=${subnet}`),
  getVulnerabilities: (id) => apiClient.get(`/api/v1/assets/${id}/vulnerabilities`),
  update: (id, data) => apiClient.patch(`/api/v1/assets/${id}`, data),
}

/**
 * Audit API
 */
export const auditApi = {
  list: (params = {}) => apiClient.get('/api/v1/audit/', { params }),
  stats: () => apiClient.get('/api/v1/audit/stats'),
  export: (params = {}) => apiClient.get('/api/v1/audit/export', { params }),
}

/**
 * Notifications API
 */
export const notificationsApi = {
  list: (limit = 20) => apiClient.get('/api/v1/notifications/', { params: { limit } }),
  markRead: (id) => apiClient.post(`/api/v1/notifications/${id}/read`),
  markAllRead: () => apiClient.post('/api/v1/notifications/read-all'),
}

/**
 * Compliance API
 */
export const complianceApi = {
  frameworks: () => apiClient.get('/api/v1/compliance/frameworks'),
  summary: () => apiClient.get('/api/v1/compliance/summary'),
  map: (vulnId) => apiClient.get(`/api/v1/compliance/map/${vulnId}`),
  downloadReport: () => apiClient.get('/api/v1/compliance/report/download', { responseType: 'blob' }),
  exportEvidence: () => apiClient.get('/api/v1/compliance/evidence/export', { responseType: 'blob' }),
  score: () => apiClient.get('/api/v1/compliance/score'),
  evidenceCoverage: () => apiClient.get('/api/v1/compliance/evidence/coverage'),
  oscalPoam: (params = {}) => apiClient.get('/api/v1/compliance/oscal/poam', { params }),
  downloadOscalPoam: () => apiClient.get('/api/v1/compliance/oscal/poam', { params: { download: true }, responseType: 'blob' }),
}

/**
 * Activity API
 */
export const activityApi = {
  summary: (hours = 24) => apiClient.get('/api/v1/activity/summary', { params: { hours } }),
}

/**
 * SLA API
 */
export const slaApi = {
  check: () => apiClient.get('/api/v1/tickets/sla/check'),
}

/**
 * Scanner API - Multi-scanner vulnerability scanning
 */
export const scannerApi = {
  /** List available scanners with capabilities */
  listScanners: () => apiClient.get('/api/v1/scanner/scanners'),
  /** Get scan profiles for a specific scanner */
  getScannerConfigs: (scannerName) => apiClient.get(`/api/v1/scanner/scanners/${scannerName}/configs`),
  /** Start a new scan */
  startScan: (data) => apiClient.post('/api/v1/scanner/scan', data),
  /** List scan history (paginated) */
  listScans: (params) => apiClient.get('/api/v1/scanner/scans', { params }),
  /** Get scan details by ID */
  getScan: (id) => apiClient.get(`/api/v1/scanner/scans/${id}`),
  /** Cancel a scan */
  cancelScan: (id) => apiClient.delete(`/api/v1/scanner/scans/${id}`),
  /** Re-run a scan */
  rescan: (id) => apiClient.post(`/api/v1/scanner/scans/${id}/rescan`),
  /** Test OpenVAS connection */
  testConnection: () => apiClient.post('/api/v1/scanner/test-connection'),
}

/**
 * Threat Feed API — CISA KEV, NVD, EPSS auto-sync
 */
export const feedsApi = {
  status: () => apiClient.get('/api/v1/feeds/status'),
  config: () => apiClient.get('/api/v1/feeds/config'),
  sync: () => apiClient.post('/api/v1/feeds/sync'),
}

/**
 * Activity Events API — platform transparency
 */
export const activityEventsApi = {
  list: (params = {}) => apiClient.get('/api/v1/activity-events/', { params }),
  getEntityEvents: (entityType, entityId) => apiClient.get(`/api/v1/activity-events/entity/${entityType}/${entityId}`),
  getUnread: () => apiClient.get('/api/v1/activity-events/unread'),
  markRead: (data) => apiClient.post('/api/v1/activity-events/mark-read', data),
}

/**
 * Global Search API
 */
export const searchApi = {
  search: (q, limit = 20) => apiClient.get('/api/v1/search/', { params: { q, limit } }),
}

/**
 * Demo API - One-click live demo
 */
export const demoApi = {
  run: () => apiClient.post('/api/v1/demo/run'),
  status: () => apiClient.get('/api/v1/demo/status'),
}

/**
 * Agent API
 */
export const agentApi = {
  installerCapabilities: () => apiClient.get('/api/v1/agent/installers/capabilities'),
  downloadInstaller: (format, data = {}) => apiClient.post(`/api/v1/agent/installers/${format}`, data, { responseType: 'blob' }),
  installerStatus: (id) => apiClient.get(`/api/v1/agent/installers/status/${id}`),
  startManaging: (id) => apiClient.post(`/api/v1/agent/installers/status/${id}/manage`),
  list: () => apiClient.get('/api/v1/agent/list'),
  get: (id) => apiClient.get(`/api/v1/agent/${id}`),
  createToken: (data) => apiClient.post('/api/v1/agent/enrollment-token', data),
  revoke: (id) => apiClient.post(`/api/v1/agent/${id}/revoke`),
  tasks: (params = {}) => apiClient.get('/api/v1/agent/tasks/all', { params }),
  cancelTask: (taskId) => apiClient.post(`/api/v1/agent/tasks/${taskId}/cancel`),
}

/**
 * Jira Integration API
 */
export const jiraApi = {
  testConnection: () => apiClient.post('/api/v1/changes/jira/test'),
  configure: (data) => apiClient.post('/api/v1/changes/jira/configure', data),
  createIssue: (data) => apiClient.post('/api/v1/changes/jira/create-issue', data),
  syncTicket: (ticketId) => apiClient.post(`/api/v1/changes/jira/sync-ticket/${ticketId}`),
}

/**
 * Platform / Onboarding API
 */
export const platformApi = {
  onboardingStatus: () => apiClient.get('/api/v1/platform/onboarding/status'),
  concurrency: () => apiClient.get('/api/v1/platform/concurrency'),
}

/**
 * Cloud Posture Management API
 */
export const cloudPostureApi = {
  getSummary: () => apiClient.get('/api/v1/cloud-posture/summary'),
  getMisconfigurations: (params = {}) => apiClient.get('/api/v1/cloud-posture/misconfigurations', { params }),
  fixMisconfiguration: (vulnId, format = 'terraform') =>
    apiClient.post(`/api/v1/cloud-posture/fix/${vulnId}`, null, { params: { format } }),
}

/**
 * Attack Path Analysis API
 */
export const attackPathsApi = {
  getGraph: () => apiClient.get('/api/v1/attack-paths/graph'),
  getCritical: (params = {}) => apiClient.get('/api/v1/attack-paths/critical', { params }),
  getStats: () => apiClient.get('/api/v1/attack-paths/stats'),
}

/**
 * Compensating Controls API
 */
export const compensatingControlsApi = {
  list: (params = {}) => apiClient.get('/api/v1/compensating-controls/', { params }),
  get: (id) => apiClient.get(`/api/v1/compensating-controls/${id}`),
  recommend: (vulnId, data = {}) => apiClient.post(`/api/v1/compensating-controls/recommend/${vulnId}`, data),
  generate: (data) => apiClient.post('/api/v1/compensating-controls/generate', data),
  deploy: (id) => apiClient.post(`/api/v1/compensating-controls/${id}/deploy`),
  validate: (id) => apiClient.post(`/api/v1/compensating-controls/${id}/validate`),
  deactivate: (id) => apiClient.delete(`/api/v1/compensating-controls/${id}`),
  coverage: (vulnId) => apiClient.get(`/api/v1/compensating-controls/coverage/${vulnId}`),
}

/**
 * ML Scoring API
 */
export const mlScoringApi = {
  score: (vulnId) => apiClient.get(`/api/v1/ml/score/${vulnId}`),
  model: () => apiClient.get('/api/v1/ml/model'),
  train: () => apiClient.post('/api/v1/ml/train'),
}

/**
 * Remediation Metering API — per-fix billing
 */
export const meteringApi = {
  usage: (params = {}) => apiClient.get('/api/v1/metering/usage', { params }),
  billing: (params = {}) => apiClient.get('/api/v1/metering/billing', { params }),
}

/**
 * Safety Dashboard API — remediation safety record & warranty
 */
export const safetyApi = {
  record: () => apiClient.get('/api/v1/safety/record'),
  warranty: () => apiClient.get('/api/v1/safety/warranty'),
}

// Export the axios instance for custom requests
export default apiClient
